/**
 * Guide Me — lazy tour catalog (intent-driven loading).
 *
 * Design:
 *   • A lightweight {@link TourSummary} manifest is the only thing pulled
 *     into the initial bundle. It carries metadata the menu / hint pill /
 *     route-matching need (id, pluginId, title, summary, route, guidePath)
 *     but **not** the parsed step bodies.
 *   • Tour bodies live in their own Vite chunks, registered through
 *     `import.meta.glob(..., { eager: false })`. They load only when an
 *     "intent" trigger fires:
 *       1. The user navigates into a plugin           → {@link preloadPlugin}
 *       2. The Guide Me menu opens                    → {@link preloadPlugin}
 *       3. The user clicks "Play tour"                → {@link loadTourById}
 *   • Locale overlays follow the same lazy pattern, keyed by base id +
 *     locale tag, deduped by an in-flight promise cache.
 *
 * Adding a new tour is a single change: drop the file under `tours/<plugin>/`
 * and append one line to {@link TOUR_MANIFEST} below. The body loader is
 * resolved automatically against the file path you list there.
 *
 * For ~100 tours this design ships ~5 KB of metadata in the main chunk and
 * defers ~MB of step bodies + parse/validate CPU until the user expresses
 * intent to use them.
 */

import { parseTour, validateTour, attachLocaleOverlay, parseLocaleOverlay } from '@empowernow/etl-tour';
import type { Tour } from '@empowernow/etl-tour';
import { TRAINING_TOURS } from './trainingTours.generated';

/* ─────────────────────────── public types ─────────────────────────── */

export interface TourSummary {
  readonly id: string;
  readonly pluginId: string;
  readonly title: string;
  readonly summary?: string;
  readonly route?: string;
  readonly routePattern?: string;
  readonly guidePath?: string;
  /**
   * Mirrors the {@link Tour} shape — convenience for callers that already
   * read `tour.match?.route` / `tour.match?.routePattern`. Populated from
   * the same fields above; `undefined` when neither is set.
   */
  readonly match?: { readonly route?: string; readonly routePattern?: string };
  /** Filled in after the body has been loaded at least once. */
  readonly stepCount?: number;
}

interface TourSourceRef {
  readonly kind: 'etl' | 'json';
  readonly path: string;
}

interface TourManifestEntry {
  readonly id: string;
  readonly pluginId: string;
  readonly title: string;
  readonly summary?: string;
  readonly route?: string;
  readonly routePattern?: string;
  readonly guidePath?: string;
  readonly source: TourSourceRef;
  /** locale tag → overlay file path (glob-key). */
  readonly localeOverlays?: Readonly<Record<string, string>>;
}

/* ─────────────────────── lazy body / overlay registry ──────────────── */

type RawLoader = () => Promise<unknown>;

const ETL_LOADERS = import.meta.glob('./tours/**/*.etl', {
  query: '?raw',
  import: 'default',
  eager: false,
}) as Record<string, () => Promise<string>>;

const JSON_LOADERS = import.meta.glob('./tours/**/*.tour.json', {
  eager: false,
}) as Record<string, () => Promise<{ default: unknown }>>;

function resolveLoader(ref: TourSourceRef): RawLoader {
  if (ref.kind === 'etl') {
    const fn = ETL_LOADERS[ref.path];
    if (!fn) throw new Error(`[guide-me] no ETL loader registered for "${ref.path}"`);
    return fn;
  }
  const fn = JSON_LOADERS[ref.path];
  if (!fn) throw new Error(`[guide-me] no JSON loader registered for "${ref.path}"`);
  return fn;
}

/* ───────────────────────────── manifest ────────────────────────────── */

/**
 * Hand-authored summary metadata. Keep entries short — anything heavier
 * (locale-aware titles, step body, `pace` config) lives inside the file at
 * `source.path` and is parsed lazily on first use.
 *
 * Order matters: it controls the order tours appear in the Guide Me menu
 * and the order {@link findTourSummaryFor} considers when route-matching.
 */
const TOUR_MANIFEST: ReadonlyArray<TourManifestEntry> = [
  // Per-step partner-training tours (auto-generated) — one specific tour per lab
  // step so each "Show me in Experience" starts a tour for that exact use case.
  ...(TRAINING_TOURS as TourManifestEntry[]),
  {
    id: 'identity-core-tour-the-screen',
    pluginId: 'identity-core',
    title: 'Tour the IdP Users screen',
    summary:
      'Take a 30-second walk through the cards, search, status filter, and per-row actions.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#reading-the-dashboard',
    source: { kind: 'etl', path: './tours/identity-core/idp-user-management.etl' },
  },
  {
    id: 'identity-core-create-auth-account',
    pluginId: 'identity-core',
    title: 'Create an authentication account',
    summary:
      'Watch the cursor open the wizard, fill every field with demo data, and explain each one — stops before submitting.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#create-auth-account',
    source: { kind: 'etl', path: './tours/identity-core/create-auth-account.etl' },
    localeOverlays: {
      es: './tours/identity-core/create-auth-account-es.etl',
      de: './tours/identity-core/create-auth-account-de.etl',
    },
  },
  {
    id: 'identity-core-find-a-user',
    pluginId: 'identity-core',
    title: 'Find a specific user',
    summary:
      'Use the search box and status filter together to narrow the table down to the user you want.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#search-and-filter',
    source: { kind: 'json', path: './tours/identity-core/find-a-user.tour.json' },
  },
  {
    id: 'identity-core-reset-a-password',
    pluginId: 'identity-core',
    title: "Reset a user's password",
    summary:
      "Find a user and stop on their Reset button — I'll explain the modal but won't actually reset the password.",
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#reset-password',
    source: { kind: 'json', path: './tours/identity-core/reset-a-password.tour.json' },
  },
  {
    id: 'identity-core-disable-a-user',
    pluginId: 'identity-core',
    title: 'Disable a user account',
    summary:
      "Find the row, point at the Disable button, and stop — so the user can't login until re-enabled.",
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#disable-enable',
    source: { kind: 'json', path: './tours/identity-core/disable-a-user.tour.json' },
  },
  {
    id: 'identity-core-link-unlinked-account',
    pluginId: 'identity-core',
    title: 'Link an unlinked auth account',
    summary:
      'Find a row whose "Linked Person" column is empty and use Create & Link to mint a person identity.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#link-to-person',
    source: { kind: 'json', path: './tours/identity-core/link-unlinked-account.tour.json' },
  },
  {
    id: 'identity-core-audit-idp-users',
    pluginId: 'identity-core',
    title: 'v2 demo: audit a user (params + control flow)',
    summary:
      'ETL v2 showcase — params, ${interpolation}, macros, if/else, forEach with try/catch, expect, highlight, multi-spot, annotate.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html',
    source: { kind: 'etl', path: './tours/identity-core/audit-idp-users.etl' },
  },
  {
    id: 'oauth-clients-register-a-client',
    pluginId: 'clients-center',
    title: 'Register an OAuth Client',
    summary:
      'See how to filter the OAuth client list, find the create button, and where the registration form lives.',
    route: '/idp/clients-center',
    guidePath: 'oauth-clients/register-a-client.html',
    source: { kind: 'json', path: './tours/oauth-clients/register-a-client.tour.json' },
  },
  {
    id: 'token-studio-personal-access-tokens',
    pluginId: 'token-studio',
    title: 'Personal Access Tokens',
    summary:
      'Walks through Token Studio: switch between PAT and IAT tabs, find the issue button, and explain the form.',
    route: '/idp/token-studio',
    guidePath: 'token-studio/personal-access-tokens.html',
    source: { kind: 'json', path: './tours/token-studio/personal-access-tokens.tour.json' },
  },
  /* ── Home dashboard ────────────────────────────────────────────── */
  {
    id: 'home-tour-the-dashboard',
    pluginId: 'home',
    title: 'Tour the home dashboard',
    summary:
      'The launcher (Ctrl+K), your apps grid, and where plugin widgets appear below the fold.',
    route: '/',
    source: { kind: 'etl', path: './tours/home/tour-the-dashboard.etl' },
  },
  /* ── Admin plugin overview tours ───────────────────────────────── */
  {
    id: 'app-admin-tour-the-screen',
    pluginId: 'app-admin',
    title: 'Tour the App Admin dashboard',
    summary:
      'Quick Actions — deputies, the approvals inbox, inline PDP access check; Grant Access appears only in app context.',
    route: '/app-admin/owner',
    source: { kind: 'etl', path: './tours/app-admin/tour-the-screen.etl' },
  },
  {
    id: 'approval-admin-tour-the-screen',
    pluginId: 'approval-admin',
    title: 'Tour the Approvals dashboard',
    summary:
      "See where pending approvals live, where your own requests show up, and how to switch between the two.",
    route: '/approvals',
    source: { kind: 'etl', path: './tours/approval-admin/tour-the-screen.etl' },
  },
  {
    id: 'sentinel-manager-create-a-sentinel',
    pluginId: 'sentinel-manager',
    title: 'Create your first Sentinel',
    summary:
      'A 3-minute walkthrough of the create wizard — pick a Capability Pack, set the heartbeat schedule, and hand off at Create.',
    route: '/sentinel-manager',
    guidePath: 'sentinel-manager/creating-a-sentinel.html',
    source: { kind: 'etl', path: './tours/sentinel-manager/create-a-sentinel.etl' },
  },
  {
    id: 'azure-app-admin-tour-the-screen',
    pluginId: 'azure-app-admin',
    title: 'Tour the Workload Identity Command Center',
    summary:
      'Register workloads from the primary CTA; exporting builds evidence once Graph returns applications.',
    route: '/azure-app-admin',
    source: { kind: 'etl', path: './tours/azure-app-admin/tour-the-screen.etl' },
  },
  {
    id: 'collections-admin-tour-the-screen',
    pluginId: 'collections-admin',
    title: 'Tour the Collections Admin',
    summary:
      'Always-visible Collections catalog tile; External Sync and Resource Collections tiles appear only when permitted.',
    route: '/collections-admin',
    source: { kind: 'etl', path: './tours/collections-admin/tour-the-screen.etl' },
  },
  {
    id: 'crud-config-admin-tour-the-screen',
    pluginId: 'crud-config-admin',
    title: 'Tour the CRUD Configuration Files',
    summary:
      'Card grid of live CRUD JSON files — walks approval synonyms, connectors, and reminds you every edit is tenant-wide.',
    route: '/crud-config-admin',
    source: { kind: 'etl', path: './tours/crud-config-admin/tour-the-screen.etl' },
  },
  {
    id: 'entraid-admin-tour-the-screen',
    pluginId: 'entraid-admin',
    title: 'Tour the EntraID admin dashboard',
    summary:
      'Walk the Microsoft EntraID dashboard — Create User, Create Group, and where to manage existing accounts.',
    route: '/entraid-admin',
    source: { kind: 'etl', path: './tours/entraid-admin/tour-the-screen.etl' },
  },
  {
    id: 'github-admin-tour-the-screen',
    pluginId: 'github-admin',
    title: 'Tour the GitHub Security Dashboard',
    summary:
      'See the GitHub security KPIs, where alerts surface, and how to refresh the data.',
    route: '/github-admin',
    source: { kind: 'etl', path: './tours/github-admin/tour-the-screen.etl' },
  },
  {
    id: 'idp-config-admin-tour-the-screen',
    pluginId: 'idp-config-admin',
    title: 'Tour the IdP Configuration Files',
    summary:
      'Walk each IdP config tile (Settings through MCP) — each opens one live JSON file in the editor.',
    route: '/idp-config-admin',
    source: { kind: 'etl', path: './tours/idp-config-admin/tour-the-screen.etl' },
  },
  {
    id: 'ldap-admin-demo-tour-the-screen',
    pluginId: 'ldap-admin-demo',
    title: 'Tour the LDAP directory dashboard',
    summary:
      'Users tab actions (create/import/export), then Groups tab for cohort tooling.',
    route: '/ldap-admin-demo',
    source: { kind: 'etl', path: './tours/ldap-admin/tour-the-screen.etl' },
  },
  {
    id: 'mcp-admin-tour-the-screen',
    pluginId: 'mcp-admin',
    title: 'Tour the MCP Administration',
    summary:
      'Command Center tab plus Servers and Tools — stable tab anchors for the MCP fleet UI.',
    route: '/mcp-admin',
    source: { kind: 'etl', path: './tours/mcp-admin/tour-the-screen.etl' },
  },
  {
    id: 'pdp-config-admin-tour-the-screen',
    pluginId: 'pdp-config-admin',
    title: 'Tour the PDP Configuration Files',
    summary:
      'Walk each PDP config tile — IdPs, logging, PIP registry, search backends, and global settings.',
    route: '/pdp-config-admin',
    source: { kind: 'etl', path: './tours/pdp-config-admin/tour-the-screen.etl' },
  },
  {
    id: 'res-admin-classic-tour-the-screen',
    pluginId: 'res-admin-classic',
    title: 'Tour the Resource Admin (Classic)',
    summary:
      'Spotlights the legacy shell wrapper and explains when to prefer modern PDP-backed plugins.',
    route: '/res-admin-classic',
    source: { kind: 'etl', path: './tours/res-admin-classic/tour-the-screen.etl' },
  },
  {
    id: 'routes-admin-tour-the-screen',
    pluginId: 'routes-admin',
    title: 'Tour the Routes Management',
    summary:
      'See how SPA routes are managed — your routes vs. core routes, and how to register a new one.',
    route: '/routes-admin',
    source: { kind: 'etl', path: './tours/routes-admin/tour-the-screen.etl' },
  },
  {
    id: 'sap-ias-admin-tour-the-screen',
    pluginId: 'sap-ias-admin',
    title: 'Tour the SAP IAS Administration',
    summary:
      'Live SCIM KPI row (groups tile only when authorized) — totals, activity windows, and schemas.',
    route: '/sap-ias-admin',
    source: { kind: 'etl', path: './tours/sap-ias-admin/tour-the-screen.etl' },
  },
  {
    id: 'sap-rfc-admin-tour-the-screen',
    pluginId: 'sap-rfc-admin',
    title: 'Tour SAP AccessPulse',
    summary:
      'AccessPulse canvas plus the guarded View Optimization rail for licensing recommendations.',
    route: '/sap-rfc-admin',
    source: { kind: 'etl', path: './tours/sap-rfc-admin/tour-the-screen.etl' },
  },
  {
    id: 'vds-admin-tour-the-screen',
    pluginId: 'vds-admin',
    title: 'Tour the VDS Connections',
    summary:
      'See where VDS connections are listed, how to search them, and how to add a new one.',
    route: '/vds-admin',
    source: { kind: 'etl', path: './tours/vds-admin/tour-the-screen.etl' },
  },
  /* ── Agent Hub (order: specific /agents/* routes before gallery) ─ */
  {
    id: 'agent-hub-tour-discovery',
    pluginId: 'agent-hub',
    title: 'Tour Agent Discovery',
    summary:
      'KPI cards, smart filters, provider rollup, and the enroll path for observed agents.',
    route: '/agents/discovery',
    guidePath: 'agent-hub/index.html',
    source: { kind: 'etl', path: './tours/agent-hub/tour-discovery.etl' },
  },
  {
    id: 'agent-hub-tour-delegations',
    pluginId: 'agent-hub',
    title: 'Tour My Delegations',
    summary:
      'Welcome tips, search and filters, and the table of grants you have issued to agents.',
    route: '/agents/delegations',
    guidePath: 'agent-hub/delegations.html',
    source: { kind: 'etl', path: './tours/agent-hub/tour-delegations.etl' },
  },
  {
    id: 'agent-hub-tour-the-gallery',
    pluginId: 'agent-hub',
    title: 'Tour the Agents gallery',
    summary:
      'Main tabs, search and filters, and the primary actions for launching or importing agents.',
    routePattern: '^/agents$',
    guidePath: 'agent-hub/agent-gallery.html',
    source: { kind: 'etl', path: './tours/agent-hub/tour-the-gallery.etl' },
  },
  {
    id: 'dc-ui-tour-the-screen',
    pluginId: 'dc-ui',
    title: 'Tour the Data Collector',
    summary:
      'End-to-end orientation — Home, Inventories, Configuration, Analytics, and the Entities Explorer.',
    route: '/dc-ui',
    guidePath: 'data-collector/data-collector.html#concepts',
    source: { kind: 'etl', path: './tours/dc-ui/tour-the-screen.etl' },
  },
  {
    id: 'dc-ui-tour-inventories',
    pluginId: 'dc-ui',
    title: 'Tour the Inventories page',
    summary:
      'Filter bar, Existing-only and Running-only toggles, Disable All, every column (System Name, Status, Inventory Status, Health, Next Trigger, Entities, Next Inventory Input, Actions), and the offline empty state.',
    routePattern: '/dc-ui/systems',
    guidePath: 'data-collector/data-collector.html#inventories',
    source: { kind: 'etl', path: './tours/dc-ui/tour-inventories.etl' },
  },
  {
    id: 'dc-ui-tour-configuration',
    pluginId: 'dc-ui',
    title: 'Tour Configuration',
    summary:
      'All six Configuration pages — Systems (with the tab-based System Designer), System Types, Schemas, Connectors, Strategies, Credentials — plus what each one is for and how they depend on each other.',
    route: '/dc-ui',
    guidePath: 'data-collector/data-collector.html#configuration',
    source: { kind: 'etl', path: './tours/dc-ui/tour-configuration.etl' },
  },
  {
    id: 'dc-ui-tour-analytics',
    pluginId: 'dc-ui',
    title: 'Tour Analytics',
    summary:
      'Inventory Runs (filters, columns, trigger modes, the run detail page with Gantt timeline), Log Viewer, Cross-Inventory pivot, and Admin Events audit trail.',
    routePattern: '/dc-ui/analytics',
    guidePath: 'data-collector/data-collector.html#analytics',
    source: { kind: 'etl', path: './tours/dc-ui/tour-analytics.etl' },
  },
  {
    id: 'dc-ui-tour-entities',
    pluginId: 'dc-ui',
    title: 'Tour the Entities Explorer',
    summary:
      'System / entity-type pickers, the grid columns including store-prefixed source columns, the entity details drawer, the Lineage tab, and how layout persists per user per system.',
    routePattern: '/dc-ui/entities-explorer',
    guidePath: 'data-collector/data-collector.html#entities',
    source: { kind: 'etl', path: './tours/dc-ui/tour-entities.etl' },
  },
  /* ── Partner-training orientation tour (anchor-free, robust) ──────── */
  {
    id: 'identity-core-training-orient',
    pluginId: 'identity-core',
    title: 'Identity & Access — orient & create',
    summary:
      'Walk the IdP Users screen — the summary cards, search, the Create Auth Account wizard, and where resolved people appear.',
    route: '/identity-core/idp-users',
    guidePath: 'identity-core/idp-user-management.html#reading-the-dashboard',
    source: { kind: 'etl', path: './tours/identity-core/training-orient.etl' },
  },
  /* ── Data Collector (dc-connect — the new Data Collector app) ─────── */
  {
    id: 'dc-connect-tour-the-screen',
    pluginId: 'dc-connect',
    title: 'Tour the Data Collector app',
    summary:
      'A quick walk through the DC Connect landing — the four tabs, the five KPI cards, and New system.',
    route: '/dc-connect',
    guidePath: 'data-collector/dc-connect.html#tour',
    source: { kind: 'etl', path: './tours/dc-connect/tour-the-screen.etl' },
  },
  {
    id: 'dc-connect-new-connector',
    pluginId: 'dc-connect',
    title: 'Build a REST connector',
    summary:
      'Walk the New connector wizard — Choose, Connect, Entities, Schedule, Review — to author a REST connector.',
    route: '/dc-connect/profiles/new',
    guidePath: 'data-collector/dc-connect.html#new-connector',
    source: { kind: 'etl', path: './tours/dc-connect/new-connector.etl' },
  },
  {
    id: 'dc-connect-new-system',
    pluginId: 'dc-connect',
    title: 'Register a system (New system)',
    summary:
      'Drive the New system wizard — pick a source, name it, and Test connection. Stops before Create.',
    route: '/dc-connect/register',
    guidePath: 'data-collector/dc-connect.html#new-system',
    source: { kind: 'etl', path: './tours/dc-connect/new-system.etl' },
  },
  /* ── System Editor (system-editor — the CRUD / write path) ────────── */
  {
    id: 'system-editor-tour-the-screen',
    pluginId: 'system-editor',
    title: 'Tour the System Editor',
    summary:
      'Orientation of the write-path app — Create New System, the Systems/Types view, and the editor tabs.',
    route: '/system-editor',
    guidePath: 'system-editor/system-editor.html#tour',
    source: { kind: 'etl', path: './tours/system-editor/tour-the-screen.etl' },
  },
  {
    id: 'system-editor-systems-list',
    pluginId: 'system-editor',
    title: 'Tour the System Definitions list',
    summary:
      'The write-path landing — the two create entry points, search + type filter, and the Grid / Table / Tree / Mind Map views.',
    route: '/system-editor',
    guidePath: 'system-editor/system-editor.html#systems-list',
    source: { kind: 'etl', path: './tours/system-editor/systems-list.etl' },
  },
  {
    id: 'system-editor-new-system-type',
    pluginId: 'system-editor',
    title: 'Create a system type (custom connector)',
    summary:
      'Drive the New System Type wizard — pick a connector kind (REST), fill the type details and object model, and reach AI Generate. Stops before Save & Open.',
    route: '/system-editor/type/new',
    guidePath: 'system-editor/system-editor.html#new-system-type',
    source: { kind: 'etl', path: './tours/system-editor/new-system-type.etl' },
  },
  {
    id: 'system-editor-new-system',
    pluginId: 'system-editor',
    title: 'Create a system (New System wizard)',
    summary:
      'Drive the New System wizard — open it, filter the type catalog, then pick a type, configure + test the Connection, and Apply & Save.',
    route: '/system-editor/new',
    guidePath: 'system-editor/system-editor.html#new-system',
    source: { kind: 'etl', path: './tours/system-editor/new-system.etl' },
  },
  /* ── Orchestration Studio (visual-designer) ──────────────────────── */
  {
    id: 'visual-designer-tour-the-dashboard',
    pluginId: 'visual-designer',
    title: 'Tour the Orchestration Studio',
    summary:
      'Read the gallery — search, status filter, sort, view toggles, the Create button, and what each workflow card tells you.',
    route: '/visual-designer',
    guidePath: 'orchestration-studio/workflow-gallery.html#reading-gallery',
    source: { kind: 'etl', path: './tours/visual-designer/tour-the-dashboard.etl' },
  },
  {
    id: 'visual-designer-create-a-workflow',
    pluginId: 'visual-designer',
    title: 'Create a new workflow',
    summary:
      'Open Orchestration Studio, click + Create New Workflow, and land on a fresh canvas with the Start node already placed.',
    route: '/visual-designer',
    guidePath: 'orchestration-studio/creating-your-first-workflow.html#create',
    source: { kind: 'etl', path: './tours/visual-designer/create-a-workflow.etl' },
  },
  {
    id: 'visual-designer-tour-the-mindmap',
    pluginId: 'visual-designer',
    title: 'Tour the Workflow Architecture Mind Map',
    summary:
      'Explore the mind-map view — five layouts (Radial, Tree, Grid, Cluster, Spiral), the Types / Systems / Workflows / Forms stat pills, and the drag / zoom / right-click interactions.',
    route: '/visual-designer',
    guidePath: 'orchestration-studio/workflow-gallery.html#reading-gallery',
    source: { kind: 'etl', path: './tours/visual-designer/tour-the-mindmap.etl' },
  },
  {
    id: 'visual-designer-tour-the-canvas',
    pluginId: 'visual-designer',
    title: 'Tour the Workflow Designer',
    summary:
      'Top bar, Node Library, canvas, JSON editor, activity rail, bottom panel, canvas controls, minimap, AI Workflow Assistant — and how to drag a node onto the canvas and connect it to another node.',
    routePattern: '/visual-designer/canvas',
    guidePath: 'orchestration-studio/designing-workflows.html#anatomy',
    source: { kind: 'etl', path: './tours/visual-designer/tour-the-canvas.etl' },
  },
  {
    id: 'visual-designer-pin-and-test-a-node',
    pluginId: 'visual-designer',
    title: 'Pin a node output and test downstream',
    summary:
      'Run a single node to capture its output, pin that output as a deterministic stand-in, and re-run the rest of the workflow against the pin without re-calling the real system.',
    routePattern: '/visual-designer/canvas',
    guidePath: 'orchestration-studio/testing-and-running.html#pin-output',
    source: { kind: 'etl', path: './tours/visual-designer/pin-and-test-a-node.etl' },
  },
  {
    id: 'visual-designer-run-a-workflow',
    pluginId: 'visual-designer',
    title: 'Run a workflow from the gallery',
    summary:
      'Use the Execute button on a workflow card to open the Workflow Runner, supply inputs, and start a run without entering the canvas.',
    route: '/visual-designer',
    guidePath: 'orchestration-studio/testing-and-running.html#runner',
    source: { kind: 'etl', path: './tours/visual-designer/run-a-workflow.etl' },
  },
  {
    id: 'visual-designer-ai-assistant',
    pluginId: 'visual-designer',
    title: 'AI Assistant — generate & edit with AI',
    summary:
      'Describe a workflow in plain English, watch the assistant draft nodes and edges, preview the result, and apply it to the canvas.',
    routePattern: '/visual-designer/canvas',
    guidePath: 'orchestration-studio/ai-workflow-assistant.html#open-ai',
    source: { kind: 'etl', path: './tours/visual-designer/ai-assistant.etl' },
  },
];

/* ─────────────────────── manifest indexing (sync) ─────────────────── */

const BY_ID: ReadonlyMap<string, TourManifestEntry> = (() => {
  const m = new Map<string, TourManifestEntry>();
  for (const e of TOUR_MANIFEST) {
    if (m.has(e.id)) {
      // Catch authoring conflicts at load time, not at runtime.
      // eslint-disable-next-line no-console
      console.error(`[guide-me] duplicate tour id "${e.id}" in TOUR_MANIFEST`);
    }
    m.set(e.id, e);
  }
  return m;
})();

const BY_PLUGIN: ReadonlyMap<string, ReadonlyArray<TourManifestEntry>> = (() => {
  const m = new Map<string, TourManifestEntry[]>();
  for (const e of TOUR_MANIFEST) {
    let arr = m.get(e.pluginId);
    if (!arr) {
      arr = [];
      m.set(e.pluginId, arr);
    }
    arr.push(e);
  }
  return m as ReadonlyMap<string, ReadonlyArray<TourManifestEntry>>;
})();

function entryToSummary(e: TourManifestEntry, stepCount?: number): TourSummary {
  return {
    id: e.id,
    pluginId: e.pluginId,
    title: e.title,
    summary: e.summary,
    route: e.route,
    routePattern: e.routePattern,
    guidePath: e.guidePath,
    match:
      e.route !== undefined || e.routePattern !== undefined
        ? { route: e.route, routePattern: e.routePattern }
        : undefined,
    stepCount,
  };
}

/* ─────────────────────────── caches ────────────────────────────────── */

/** id → fully-resolved Tour (after body parse + locale overlay merge). */
const tourCache = new Map<string, Promise<Tour>>();
/** Synchronous mirror — populated when the promise above resolves. */
const resolvedTourCache = new Map<string, Tour>();
/** id|locale → overlay-merged Tour. Keyed separately so locale switches stay cheap. */
const localizedCache = new Map<string, Promise<Tour>>();

function resolvedKey(id: string, localeTag: string | undefined): string {
  return localeTag ? `${id} ${localeTag}` : id;
}

/* ───────────────────────── body resolution ─────────────────────────── */

async function resolveBody(entry: TourManifestEntry): Promise<Tour> {
  const loader = resolveLoader(entry.source);
  if (entry.source.kind === 'etl') {
    const raw = (await loader()) as string;
    return parseTour(raw);
  }
  const mod = (await loader()) as { default: unknown };
  return validateTour(mod.default);
}

function loadBaseTour(id: string): Promise<Tour> {
  const cached = tourCache.get(id);
  if (cached) return cached;
  const entry = BY_ID.get(id);
  if (!entry) {
    return Promise.reject(new Error(`[guide-me] unknown tour id "${id}"`));
  }
  const p = resolveBody(entry).then((t) => {
    resolvedTourCache.set(id, t);
    return t;
  });
  tourCache.set(id, p);
  // Don't poison the cache on transient errors — let the next call retry.
  p.catch(() => tourCache.delete(id));
  return p;
}

/* ───────────────────────── locale overlay merge ────────────────────── */

function pickOverlayTag(
  entry: TourManifestEntry,
  appResolvedLocale: string | undefined,
): string | undefined {
  const overlays = entry.localeOverlays;
  if (!overlays || !appResolvedLocale) return undefined;
  const lower = appResolvedLocale.trim().toLowerCase().replace(/_/g, '-');
  if (lower in overlays) return lower;
  const dash = lower.indexOf('-');
  const primary = dash === -1 ? lower : lower.slice(0, dash);
  if (primary in overlays) return primary;
  return undefined;
}

async function loadOverlay(path: string): Promise<string> {
  const fn = ETL_LOADERS[path];
  if (!fn) throw new Error(`[guide-me] no overlay loader for "${path}"`);
  return fn();
}

/* ────────────────────────── public read API ────────────────────────── */

export function listAllTourSummaries(): TourSummary[] {
  return TOUR_MANIFEST.map((e) =>
    entryToSummary(e, resolvedTourCache.get(e.id)?.steps.length),
  );
}

export function listTourSummariesForPlugin(pluginId: string | null): TourSummary[] {
  if (!pluginId) return [];
  const arr = BY_PLUGIN.get(pluginId);
  if (!arr) return [];
  return arr.map((e) => entryToSummary(e, resolvedTourCache.get(e.id)?.steps.length));
}

export function getTourSummaryById(id: string): TourSummary | null {
  const e = BY_ID.get(id);
  if (!e) return null;
  return entryToSummary(e, resolvedTourCache.get(id)?.steps.length);
}

/**
 * Sync-cache lookup. Returns the fully-resolved Tour if a previous load
 * succeeded; null otherwise. Prefer {@link loadTourById} when you need
 * to guarantee the tour is available.
 */
export function getCachedTour(id: string): Tour | null {
  return resolvedTourCache.get(id) ?? null;
}

/**
 * Route-match against the manifest **without** loading any bodies.
 * Mirrors the legacy `findTourFor` semantics: longest exact-prefix wins,
 * with `routePattern` as a fallback. Falls back to the plugin's first
 * tour if nothing matches and `fallbackToFirst` is true.
 */
export function findTourSummaryFor(
  pluginId: string | null,
  pathname: string,
  options: { fallbackToFirst?: boolean } = {},
): TourSummary | null {
  if (!pluginId) return null;
  const arr = BY_PLUGIN.get(pluginId);
  if (!arr || arr.length === 0) return null;

  for (const e of arr) {
    const route = e.route;
    if (route && (pathname === route || pathname.startsWith(`${route}/`))) {
      return entryToSummary(e, resolvedTourCache.get(e.id)?.steps.length);
    }
    const pattern = e.routePattern;
    if (pattern) {
      try {
        if (new RegExp(pattern).test(pathname)) {
          return entryToSummary(e, resolvedTourCache.get(e.id)?.steps.length);
        }
      } catch {
        // Manifest authoring error — silently skip; menu still works.
      }
    }
  }

  if (options.fallbackToFirst) {
    const first = arr[0];
    return entryToSummary(first, resolvedTourCache.get(first.id)?.steps.length);
  }
  return null;
}

/* ────────────────────────── public load API ────────────────────────── */

/**
 * Resolves the tour body and (if applicable) merges the locale overlay
 * matching `appResolvedLocale`. Cached per (id, localeTag) so repeated
 * loads — and concurrent ones during route-flap — dedupe to a single
 * network/parse pass.
 */
export function loadTourById(
  id: string,
  appResolvedLocale: string | undefined = undefined,
): Promise<Tour> {
  const entry = BY_ID.get(id);
  if (!entry) {
    return Promise.reject(new Error(`[guide-me] unknown tour id "${id}"`));
  }
  const tag = pickOverlayTag(entry, appResolvedLocale);
  const key = resolvedKey(id, tag);
  const cached = localizedCache.get(key);
  if (cached) return cached;

  const p = (async (): Promise<Tour> => {
    const base = await loadBaseTour(id);
    if (!tag) return base;
    const overlayPath = entry.localeOverlays?.[tag];
    if (!overlayPath) return base;
    try {
      const raw = await loadOverlay(overlayPath);
      const merged = attachLocaleOverlay(base, tag, parseLocaleOverlay(raw));
      resolvedTourCache.set(id, merged); // upgrade sync mirror to localized version
      return merged;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        `[guide-me] failed to load locale overlay for "${id}" (${tag}):`,
        err,
      );
      return base;
    }
  })();

  localizedCache.set(key, p);
  p.catch(() => localizedCache.delete(key));
  return p;
}

/**
 * Trigger #1 / #2: kick body loads for every tour belonging to a plugin.
 * Returns a promise that resolves once **all** tours are in cache (so the
 * menu can render step-counts), but the function itself returns
 * immediately to the caller — perfect for fire-and-forget on route
 * change. Idempotent: subsequent calls just await the same promises.
 */
export function preloadPlugin(
  pluginId: string | null,
  appResolvedLocale: string | undefined = undefined,
): Promise<Tour[]> {
  if (!pluginId) return Promise.resolve([]);
  const arr = BY_PLUGIN.get(pluginId);
  if (!arr || arr.length === 0) return Promise.resolve([]);
  return Promise.all(arr.map((e) => loadTourById(e.id, appResolvedLocale)));
}

/**
 * Fire-and-forget variant useful from `useEffect` blocks — never throws
 * to the caller; errors are logged and individual failures don't block
 * sibling tours from loading.
 */
export function preloadPluginSilent(
  pluginId: string | null,
  appResolvedLocale: string | undefined = undefined,
): void {
  preloadPlugin(pluginId, appResolvedLocale).catch((err) => {
    // eslint-disable-next-line no-console
    console.error(`[guide-me] preloadPlugin("${pluginId ?? 'null'}") failed:`, err);
  });
}

/** Test / HMR aid. Drops every cached body and overlay. */
export function clearCatalogCache(): void {
  tourCache.clear();
  resolvedTourCache.clear();
  localizedCache.clear();
}
